import { getBackendUrl } from '@/lib/backendUrl';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { beverageProductsService, measureDefinitionsService, stocktakingSettingsService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import {
  Plus, Search, Loader2, Save, Trash2, X, Package, ChevronLeft, ChevronRight,
  Pencil, Upload, FileSpreadsheet, CheckCircle2, AlertCircle, ArrowUp, ArrowDown,
  ArrowUpDown, ArrowLeft, Info
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';
import { getApiError } from '@/lib/utils';
import axios from 'axios';
import {
  AddProductFormBody, UnitRow, emptyUnit,
  handleUnitChange as _sharedHandleUnitChange,
} from '../components/shared/AddProductFormBody';
import { DuplicateReviewPanel, DUPLICATE_REASON_MIN } from '../components/shared/DuplicateReviewPanel';
import { ImpactReviewDialog } from '../components/shared/ImpactReviewDialog';
import { measureTypeOf } from '../components/shared/measureType';

// Stage 1A — transient form-only keys never sent to the API
const _TRANSIENT_UNIT_KEYS = ['_units_per_case', '_indiv_ml', '_orig_net_grams', '_orig_net_grams_evidence',
  'net_grams_reviewed_at', 'net_grams_reviewed_by_name', 'net_grams_reviewed_by_user_id', 'measure_type', '_customDraft', '_ai_unit_label'];
const _stripUnit = (u) => {
  const out = { ...u };
  _TRANSIENT_UNIT_KEYS.forEach(k => delete out[k]);
  return out;
};

const CATEGORIES = [
  { value: 'beer', label: 'Beer' },
  { value: 'wine', label: 'Wine' },
  { value: 'spirits', label: 'Spirits' },
  { value: 'soft_drinks', label: 'Soft Drinks' },
  { value: 'cider', label: 'Cider' },
  { value: 'mixers', label: 'Mixers' },
  { value: 'other', label: 'Other' },
];

// Fallback unit types (used until dynamic measures load)
const DEFAULT_UNIT_TYPES = ['bottle', 'keg', 'case', 'litre', 'ml', 'each', 'can', 'pint', 'half_pint', 'glass', 'shot', 'gallon', 'optic'];
const DEFAULT_UNIT_LABELS = { bottle: 'Bottle', keg: 'Keg', case: 'Case', litre: 'Litre', ml: 'ml', each: 'Each', can: 'Can', pint: 'Pint', half_pint: 'Half Pint', glass: 'Glass', shot: 'Shot', gallon: 'Gallon', optic: 'Optic' };


const emptyYield = () => ({
  yield_type: '', servings_per_unit: '', ml_per_serving: '',
  nominal_servings_per_unit: '', nominal_ml_per_serving: '',
  waste_factor: '', is_default: false,
});

const emptyProduct = () => ({
  product_code: '', product_name: '', brand: '', category: '',
  subcategory: '', category_id: null, subcategory_id: null,
  standard_cost: '', barcode: '', case_size: '', status: 'active',
  internal_note: '',
  is_post_mix: false,
  // iter_postmix v2 — serving-measure-aware yield.
  // post_mix_servings_per_ref = number of servings per pmConvFactor_ml of concentrate,
  //   using the measure selected in post_mix_selected_serving_unit.
  // post_mix_finished_volume_ml is computed from this on save.
  post_mix_servings_per_ref: '',
  post_mix_selected_serving_unit: '',  // unit_type string from product.units
  post_mix_servings_per_unit: '',    // computed on save
  post_mix_serving_ml: '',           // computed on save (ml of the selected serving unit)
  // count-based mode: purchase unit has no ml — user enters servings/unit directly.
  pm_direct_servings_per_unit: '',
  units: [{ ...emptyUnit(), is_default_count_unit: true }],
  yields: [{ ...emptyYield(), is_default: true }],
});

// ── Inline Unit Row ──────────────────────────────────────────────
// ── Inline Yield Row ─────────────────────────────────────────────
function YieldRow({ yld, index, onChange, onRemove, isOnly, units, isCountBased }) {
  const update = (field, val) => onChange(index, { ...yld, [field]: val });
  // iter420 — Auto-compute nominal_servings_per_unit from the count Unit's
  // ml ÷ this yield's ml_per_serving. Read-only display. The engine still
  // reads the persisted value when present, so manual overrides win; the
  // computed value is just to remove the friction of typing it.
  const countUnit = (units || []).find(u => u.is_default_count_unit && u.conversion_to_base);
  const countMl = countUnit ? parseFloat(countUnit.conversion_to_base) : null;
  const yldMl = parseFloat(yld.ml_per_serving);
  const autoNominal = (countMl && yldMl && yldMl > 0)
    ? Math.round((countMl / yldMl) * 100) / 100
    : null;
  return (
    <TableRow data-testid={`yield-row-${index}`}>
      <TableCell className="py-1.5">
        <Input className="h-8 text-xs" value={yld.yield_type} placeholder="e.g. Pint"
          onChange={(e) => update('yield_type', e.target.value)} data-testid={`yield-type-${index}`} />
      </TableCell>
      <TableCell className="py-1.5">
        {/* iter420 — Read-only auto-computed nominal. Falls back to the
            persisted value (e.g. Excel import) when units aren't set up
            yet. Admins can still override by clicking the lock icon. */}
        <div className="flex items-center gap-1">
          <Input
            type="number"
            readOnly
            className="h-8 text-xs w-20 bg-slate-50 text-slate-700"
            value={autoNominal != null ? String(autoNominal) : (yld.nominal_servings_per_unit || '')}
            title={autoNominal != null
              ? `Auto: ${countUnit?.unit_type || 'count unit'} ${countMl}ml ÷ ${yldMl}ml = ${autoNominal}`
              : 'Set the count unit ml + ml/serving to auto-compute'}
            data-testid={`yield-nominal-servings-${index}`}
          />
          {yld.nominal_derivation && (
            <span
              title={`Derived ${yld.nominal_derivation.derived_at ? yld.nominal_derivation.derived_at.split('T')[0] : ''}: ${yld.nominal_derivation.formula}`}
              className={`text-[10px] px-1.5 py-0.5 rounded border ${yld.nominal_derivation.stale ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}
              data-testid={`yield-nominal-derived-badge-${index}`}
            >
              {yld.nominal_derivation.stale ? 'Stale' : 'Derived'}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="py-1.5">
        <Input type="number" className={`h-8 text-xs w-20 ${parseFloat(yld.servings_per_unit) > 50 && !yld.ml_per_serving && !isCountBased ? 'border-amber-400 ring-1 ring-amber-200' : ''}`}
          value={yld.servings_per_unit}
          onChange={(e) => update('servings_per_unit', e.target.value)} placeholder="optional"
          title="Actual/observed servings per unit (optional — overrides nominal for calculations)"
          data-testid={`yield-servings-${index}`} />
        {parseFloat(yld.servings_per_unit) > 50 && !yld.ml_per_serving && !isCountBased && (
          <p className="text-[10px] text-amber-600 mt-0.5 leading-tight" data-testid={`yield-servings-warning-${index}`}>
            ⚠ Did you mean ml/Serving? Servings/Unit is rarely &gt; 50.
          </p>
        )}
      </TableCell>
      <TableCell className="py-1.5">
        <Input type="number" className="h-8 text-xs w-20" value={yld.ml_per_serving}
          onChange={(e) => update('ml_per_serving', e.target.value)} placeholder="568"
          data-testid={`yield-ml-${index}`} />
      </TableCell>
      <TableCell className="py-1.5">
        <Input type="number" className="h-8 text-xs w-20" value={yld.waste_factor}
          onChange={(e) => update('waste_factor', e.target.value)} placeholder="0.02"
          data-testid={`yield-waste-${index}`} />
      </TableCell>
      <TableCell className="py-1.5 text-center">
        <Switch checked={yld.is_default}
          onCheckedChange={(v) => onChange(index, { ...yld, is_default: v }, 'default_yield')}
          data-testid={`yield-default-${index}`} />
      </TableCell>
      <TableCell className="py-1.5 text-center">
        <Button variant="ghost" size="icon" className="h-7 w-7" disabled={isOnly}
          onClick={() => onRemove(index)} data-testid={`yield-remove-${index}`}>
          <Trash2 className="h-3.5 w-3.5 text-destructive" />
        </Button>
      </TableCell>
    </TableRow>
  );
}


// ── Main Page ────────────────────────────────────────────────────
export default function BeverageStockMasterPage() {
  // iter399 — auditors now have full master-inventory access too. Treat any
  // internal user (admin/scheduler/senior_scheduler/auditor) as able to edit.
  const { isAdmin, isScheduler, isSeniorScheduler, isAuditor } = useAuth();
  const canManage = isAdmin || isScheduler || isSeniorScheduler || isAuditor;
  const { toast } = useToast();
  const navigate = useNavigate();

  // List state
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('search') || '');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubcategory, setFilterSubcategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState(emptyProduct());
  // Stage 1A — duplicate review (create) and impact review (edit)
  const [dupCandidates, setDupCandidates] = useState([]);
  const [dupReason, setDupReason] = useState('');
  const [impactReview, setImpactReview] = useState(null);
  const [pendingPayload, setPendingPayload] = useState(null);

  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importTaskId, setImportTaskId] = useState(null);
  const [importProgress, setImportProgress] = useState(null);

  // Dynamic measure definitions from central master list
  const [dynamicUnitTypes, setDynamicUnitTypes] = useState(DEFAULT_UNIT_TYPES);
  const [dynamicUnitLabels, setDynamicUnitLabels] = useState(DEFAULT_UNIT_LABELS);
  // iter420k — Per-measure default ml map (loaded from
  // /stocktake/measure-definitions). Drives auto-populate-on-pick in
  // the Unit Row dropdown so the operator doesn't re-type 568 for
  // every Pint.
  const [dynamicUnitMlMap, setDynamicUnitMlMap] = useState({});

  // iter355b — user-defined taxonomy fetched from /api/product-taxonomy/categories.
  // Replaces the hardcoded CATEGORIES enum so custom categories created in the
  // Product Categories admin show up here too. Filter + form both read from it.
  const [taxonomyCategories, setTaxonomyCategories] = useState([]);

  const reloadTaxonomy = useCallback(async () => {
    try {
      const { data } = await axios.get(`${getBackendUrl()}/api/product-taxonomy/categories`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      setTaxonomyCategories(data?.categories || []);
    } catch (err) {
      // Keep CATEGORIES enum as a graceful fallback so the page is never blank.
      setTaxonomyCategories([]);
    }
  }, []);
  useEffect(() => { reloadTaxonomy(); }, [reloadTaxonomy]);

  // Active categories for pickers; merge with the legacy enum fallback if the
  // taxonomy endpoint hasn't returned yet (covers brand-new orgs or 401 paths).
  const categoryOptions = useMemo(() => {
    if (taxonomyCategories.length > 0) {
      return taxonomyCategories
        .filter(c => c.is_active)
        .map(c => ({
          id: c.id,
          slug: c.slug,
          label: c.name,
          is_system: c.is_system,
          subcategories: (c.subcategories || []).filter(s => s.is_active),
        }));
    }
    return CATEGORIES.map(c => ({ id: null, slug: c.value, label: c.label, is_system: true, subcategories: [] }));
  }, [taxonomyCategories]);

  const formCategoryOption = useMemo(() => {
    // iter-newport-fix: fall through to slug match when ID lookup misses
    // (taxonomy in fallback mode or still loading).
    if (form.category_id) {
      const byId = categoryOptions.find(c => c.id === form.category_id);
      if (byId) return byId;
    }
    if (form.category) {
      const lc = form.category.toLowerCase();
      return categoryOptions.find(c => (c.slug || '').toLowerCase() === lc) || null;
    }
    return null;
  }, [categoryOptions, form.category_id, form.category]);

  const filterCategoryOption = useMemo(() => {
    if (!filterCategory) return null;
    return categoryOptions.find(c => String(c.id) === String(filterCategory) || c.slug === filterCategory) || null;
  }, [categoryOptions, filterCategory]);

  const PAGE_SIZE = 25;

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      // iter355b — prefer FK filters (work for both system + custom categories).
      // Fall back to legacy enum strings if filterCategory is a slug from the
      // graceful-fallback path before taxonomy has loaded.
      if (filterCategory) {
        if (/^\d+$/.test(String(filterCategory))) {
          params.category_id = Number(filterCategory);
        } else {
          params.category = filterCategory;
        }
      }
      if (filterSubcategory) {
        if (/^\d+$/.test(String(filterSubcategory))) {
          params.subcategory_id = Number(filterSubcategory);
        } else {
          params.subcategory = filterSubcategory;
        }
      }
      if (filterStatus) params.status = filterStatus;
      if (sortBy) { params.sort_by = sortBy; params.sort_dir = sortDir; }
      const data = await beverageProductsService.list(params);
      setProducts(data.items || []);
      setTotal(data.total || 0);
      if (data.subcategories) setSubcategoryOptions(data.subcategories);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load products', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [page, search, filterCategory, filterSubcategory, filterStatus, sortBy, sortDir, toast]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  // Fetch dynamic measure definitions from central master list
  useEffect(() => {
    const loadMeasures = async () => {
      try {
        const data = await measureDefinitionsService.list();
        if (data && data.length > 0) {
          // iter420k — Sort alphabetically (case-insensitive) so the
          // dropdown is predictable. Backend returns rows by sort_order
          // then name — but operators want pure alphabetical here so
          // they can scan/find the measure quickly. Also build an
          // `ml` map keyed by the normalised type so the picker can
          // auto-populate `conversion_to_base` on selection.
          const sorted = [...data].sort((a, b) =>
            (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' })
          );
          const types = sorted.map(m => m.name.toLowerCase().replace(/\s+/g, '_'));
          const labels = {};
          const mlMap = {};
          sorted.forEach(m => {
            const key = m.name.toLowerCase().replace(/\s+/g, '_');
            labels[key] = m.name;
            if (m.default_ml != null && Number(m.default_ml) > 0) {
              mlMap[key] = Number(m.default_ml);
            }
          });
          setDynamicUnitTypes(types);
          setDynamicUnitLabels(labels);
          setDynamicUnitMlMap(mlMap);
        }
      } catch (err) {
        console.error('Failed to load measure definitions, using defaults:', err);
      }
    };
    loadMeasures();
  }, []);

  // Debounced search
  const [searchInput, setSearchInput] = useState(() => new URLSearchParams(window.location.search).get('search') || '');
  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // iter_postmix — load org-level postmix conversion factor once
  const [pmConvFactor, setPmConvFactor] = useState(1000);
  useEffect(() => {
    stocktakingSettingsService.get()
      .then(d => setPmConvFactor(d.postmix_conversion_factor_ml || 1000))
      .catch(() => {});
  }, []);

  const openCreate = () => {
    setEditingProduct(null);
    setForm(emptyProduct());
    setDupCandidates([]);
    setDupReason('');
    setDialogOpen(true);
  };

  const openEdit = async (listProduct) => {
    // Stage 1A — load the full record (internal note, reviewer names) rather
    // than the list row so nothing is dropped on re-save.
    let product = listProduct;
    try { product = await beverageProductsService.get(listProduct.id); } catch (_) { /* fall back to list row */ }
    setDupCandidates([]);
    setDupReason('');
    // iter_fix2 — Ensure taxonomy has loaded with real subcategories before
    // opening the edit dialog. Reload when:
    //   1. Taxonomy not yet loaded (initial fast-click case), OR
    //   2. Product has a category_id but the current taxonomy entry for that
    //      category has no subcategories — this indicates the taxonomy loaded
    //      in fallback/empty state (CATEGORIES enum, subcategories: []).
    const catEntry = taxonomyCategories.find(c => c.id === product.category_id);
    const needsRefresh = taxonomyCategories.length === 0 || (
      product.category_id != null && (!catEntry || (catEntry.subcategories || []).length === 0)
    );
    if (needsRefresh) {
      await reloadTaxonomy();
    }
    setEditingProduct(product);
    setForm({
      product_code: product.product_code || '',
      product_name: product.product_name || '',
      brand: product.brand || '',
      category: product.category || '',
      subcategory: product.subcategory || '',
      category_id: product.category_id || null,
      subcategory_id: product.subcategory_id || null,
      standard_cost: product.standard_cost != null ? String(product.standard_cost) : '',
      barcode: product.barcode || '',
      case_size: product.case_size != null ? String(product.case_size) : '',
      status: product.status || 'active',
      internal_note: product.internal_note || '',
      is_post_mix: product.is_post_mix || false,
      post_mix_servings_per_ref: (() => {
        // iter_postmix v2 — compute servings per pmConvFactor_ml of concentrate
        // using the stored serving_ml (or default sales unit ml).
        if (!product.is_post_mix) return '';
        const purchUnit = (product.units || []).find(u => u.is_default_purchase_unit && u.conversion_to_base);
        const purchMl = purchUnit ? parseFloat(purchUnit.conversion_to_base) : null;
        if (!purchMl || purchMl <= 0) return '';
        // Use stored post_mix_servings_per_unit if available
        const spu = product.post_mix_servings_per_unit != null ? parseFloat(product.post_mix_servings_per_unit) : null;
        if (spu != null && !isNaN(spu) && spu > 0) {
          // servings_per_ref = spu × pmConvFactor / purchMl
          return String(Math.round(spu * pmConvFactor / purchMl * 100) / 100);
        }
        // Fallback: derive from post_mix_finished_volume_ml and post_mix_serving_ml
        const finMl = product.post_mix_finished_volume_ml != null ? parseFloat(product.post_mix_finished_volume_ml) : null;
        const srvMl = product.post_mix_serving_ml != null ? parseFloat(product.post_mix_serving_ml) : null;
        if (finMl && srvMl && srvMl > 0) {
          const spuCalc = finMl / srvMl;
          return String(Math.round(spuCalc * pmConvFactor / purchMl * 100) / 100);
        }
        return '';
      })(),
      post_mix_selected_serving_unit: (() => {
        // Match stored post_mix_serving_ml to a unit in the product's unit list,
        // or fall back to the default sales unit.
        if (!product.is_post_mix) return '';
        const storedServingMl = product.post_mix_serving_ml != null ? parseFloat(product.post_mix_serving_ml) : null;
        const units = product.units || [];
        if (storedServingMl) {
          const matched = units.find(u => u.conversion_to_base != null && Math.abs(parseFloat(u.conversion_to_base) - storedServingMl) < 0.01);
          if (matched) return matched.unit_type || '';
        }
        const defSales = units.find(u => u.is_default_sales_unit && u.conversion_to_base);
        return defSales?.unit_type || '';
      })(),
      post_mix_servings_per_unit: product.post_mix_servings_per_unit != null ? String(product.post_mix_servings_per_unit) : '',
      post_mix_serving_ml: product.post_mix_serving_ml != null ? String(product.post_mix_serving_ml) : '',
      // count-based: purchase unit has no ml — load direct servings/unit
      pm_direct_servings_per_unit: (() => {
        if (!product.is_post_mix) return '';
        const purchUnit = (product.units || []).find(u => u.is_default_purchase_unit);
        const purchMl = purchUnit?.conversion_to_base ? parseFloat(purchUnit.conversion_to_base) : null;
        if (!purchMl || purchMl <= 0) {
          const spu = product.post_mix_servings_per_unit;
          return (spu != null && !isNaN(parseFloat(spu))) ? String(parseFloat(spu)) : '';
        }
        return ''; // volume-based — not used
      })(),
      units: product.units?.length
        ? product.units.map(u => ({
            id: u.id, unit_type: u.unit_type || '', base_unit: u.base_unit || 'ml',
            conversion_to_base: u.conversion_to_base != null ? String(u.conversion_to_base) : '',
            // Stage 1A — carry every stored unit field so a re-save changes nothing
            is_base_stock_unit: u.is_base_stock_unit || false,
            is_default_count_unit: u.is_default_count_unit || false,
            is_default_purchase_unit: u.is_default_purchase_unit || false,
            // iter420 — sales role flags
            is_sales_unit: u.is_sales_unit || false,
            is_default_sales_unit: u.is_default_sales_unit || false,
            // iter420 Phase 4 — yield data on the unit row
            waste_factor: u.waste_factor != null ? String(u.waste_factor) : '',
            observed_servings_per_unit: u.observed_servings_per_unit != null ? String(u.observed_servings_per_unit) : '',
            case_qty: u.case_qty ?? null,
            unit_size_ml: u.unit_size_ml ?? null,
            net_grams: u.net_grams != null ? String(u.net_grams) : '',
            net_grams_evidence: u.net_grams_evidence || '',
            _orig_net_grams: u.net_grams != null ? String(u.net_grams) : '',
            _orig_net_grams_evidence: u.net_grams_evidence || '',
            net_grams_reviewed_at: u.net_grams_reviewed_at || null,
            net_grams_reviewed_by_name: u.net_grams_reviewed_by_name || null,
          }))
        : [{ ...emptyUnit(), is_default_count_unit: true }],
      yields: product.yields?.length
        ? product.yields.map(y => ({
            id: y.id, yield_type: y.yield_type || '',
            servings_per_unit: y.servings_per_unit != null ? String(y.servings_per_unit) : '',
            ml_per_serving: y.ml_per_serving != null ? String(y.ml_per_serving) : '',
            nominal_servings_per_unit: y.nominal_servings_per_unit != null ? String(y.nominal_servings_per_unit) : '',
            nominal_ml_per_serving: y.nominal_ml_per_serving != null ? String(y.nominal_ml_per_serving) : '',
            waste_factor: y.waste_factor != null ? String(y.waste_factor) : '',
            is_default: y.is_default || false,
            nominal_derivation: y.nominal_derivation || null,
          }))
        : [{ ...emptyYield(), is_default: true }],
    });
    setDialogOpen(true);
  };

  const handleUnitChange = (idx, updated, action) => {
    setForm(prev => {
      const units = [...prev.units];
      if (action === 'default_count' && updated.is_default_count_unit) {
        units.forEach((u, i) => { if (i !== idx) u.is_default_count_unit = false; });
      }
      // Enforce single-selection for purchase unit
      if (action === 'default_purchase' && updated.is_default_purchase_unit) {
        units.forEach((u, i) => { if (i !== idx) u.is_default_purchase_unit = false; });
      }
      // iter420 — only one row can be is_default_sales_unit per product
      if (action === 'default_sales' && updated.is_default_sales_unit) {
        units.forEach((u, i) => { if (i !== idx) u.is_default_sales_unit = false; });
      }
      units[idx] = updated;
      // iter420 — When a unit becomes a sales unit, auto-stub a matching
      // Yield row (yield_type=unit_type, ml_per_serving=conversion_to_base)
      // so the engine has wastage/observed-servings slots ready without
      // forcing the admin to open Advanced. Only stubs when no yield
      // already targets that ml.
      let yields = prev.yields;
      // Stage 1A — no auto yield stub for the count pattern (each): no ml is copied.
      // 2026-11 fix — same for mass (g/kg): a 15g portion's conversion_to_base
      // is grams, not millilitres, and auto-stubbing it into ml_per_serving
      // would silently mislabel grams as ml. Mass yield display lives in the
      // separate, computed-only PortionYieldPanel instead (grams in, grams
      // out — see components/shared/PortionYieldPanel.jsx).
      if ((action === 'default_sales' || action === 'sales') && updated.is_sales_unit && measureTypeOf(updated.base_unit) === 'volume') {
        const ml = parseFloat(updated.conversion_to_base);
        if (ml && ml > 0) {
          const matchIdx = yields.findIndex(y => parseFloat(y.ml_per_serving) === ml);
          if (matchIdx === -1) {
            // No yield for this ml yet — add a fresh one.
            yields = [
              ...yields.filter(y => y.yield_type || y.ml_per_serving),  // drop the empty starter row
              {
                ...emptyYield(),
                yield_type: updated.unit_type || 'Serving',
                ml_per_serving: String(ml),
                is_default: updated.is_default_sales_unit,
              },
            ];
          } else if (action === 'default_sales') {
            // iter420j.2 — Bug fix: when the user toggles
            // Default Sales AFTER they'd already toggled Sales (which
            // pre-stubbed a yield row with is_default=false), the
            // existing yield row's is_default flag was never synced
            // — so the operator hit "Validation: One yield must be the
            // default" on save even though their UI showed pint as
            // default. Now we mirror the unit's default_sales flag
            // onto the matching yield row AND clear is_default on
            // every other yield (exactly one default at any time).
            yields = yields.map((y, i) => ({
              ...y,
              is_default: i === matchIdx ? !!updated.is_default_sales_unit : false,
            }));
          }
        }
      }
      return { ...prev, units, yields };
    });
  };

  const handleYieldChange = (idx, updated, action) => {
    setForm(prev => {
      const yields = [...prev.yields];
      if (action === 'default_yield' && updated.is_default) {
        yields.forEach((y, i) => { if (i !== idx) y.is_default = false; });
      }
      yields[idx] = updated;
      return { ...prev, yields };
    });
  };

  const handleSave = async () => {
    if (!form.product_name.trim()) {
      toast({ title: 'Validation', description: 'Product name is required', variant: 'destructive' });
      return;
    }
    if (!editingProduct && !form.subcategory_id) {
      toast({ title: 'Validation', description: 'Please select a subcategory', variant: 'destructive' });
      return;
    }
    if (!form.units.some(u => u.unit_type.trim())) {
      toast({ title: 'Validation', description: 'At least one unit is required', variant: 'destructive' });
      return;
    }
    if (!form.units.some(u => u.is_default_count_unit)) {
      toast({ title: 'Validation', description: 'One unit must be the default count unit', variant: 'destructive' });
      return;
    }
    // Release blocker — defense in depth: the checkbox is already disabled
    // and auto-cleared by the isSnackCountPattern effect, but block here
    // too in case stale state slips through (e.g. a fast double-toggle).
    if (form.is_post_mix && form.units.some(u => measureTypeOf(u.base_unit) !== 'volume' && measureTypeOf(u.base_unit) !== 'unknown')) {
      toast({ title: 'Validation', description: "Post-Mix Concentrate can't be combined with non-liquid units (weight or count — Packet/Case). Untick Post-Mix Concentrate first.", variant: 'destructive' });
      return;
    }
    // iter420 Phase 4 — yield data is now stored directly on the unit
    // row (`waste_factor`, `observed_servings_per_unit`). Products like
    // a 30L keg + 568ml pint (sales-default) need NO separate yield
    // entries — the pint→keg ratio (568/30000) tells the engine
    // exactly how many servings come out of a keg. Only enforce the
    // "one yield must be the default" rule when the operator has
    // actually entered yield rows (e.g. for a Postmix product where
    // dilution can't be inferred from the unit ratio).
    //
    // iter420j.2 — Self-heal yields/units default drift before
    // validating. If a unit is flagged `is_default_sales_unit=true`
    // and there's a yield row with the matching ml, mirror is_default
    // onto that yield (and clear it on every other yield). Stops the
    // operator from seeing the "One yield must be the default" toast
    // when their UI clearly shows the right flag — typically caused
    // by toggling Sales-then-Default in that order without us syncing
    // the auto-stubbed yield.
    const defaultSalesUnit = form.units.find(u => u.is_default_sales_unit);
    let yieldsForSave = form.yields;
    if (defaultSalesUnit) {
      const targetMl = parseFloat(defaultSalesUnit.conversion_to_base);
      const matchIdx = yieldsForSave.findIndex(y => parseFloat(y.ml_per_serving) === targetMl);
      if (matchIdx !== -1) {
        yieldsForSave = yieldsForSave.map((y, i) => ({
          ...y,
          is_default: i === matchIdx ? true : false,
        }));
      }
    }
    const filledYields = yieldsForSave.filter(y => y.yield_type && y.yield_type.trim());
    if (filledYields.length > 0 && !filledYields.some(y => y.is_default)) {
      toast({ title: 'Validation', description: 'One yield must be the default', variant: 'destructive' });
      return;
    }

    if (!editingProduct && dupCandidates.length > 0 && dupReason.trim().length < DUPLICATE_REASON_MIN) {
      toast({ title: 'Possible duplicate', description: 'Open or use the existing product, or give a reason (at least 10 characters) why this one is different.', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        internal_note: (form.internal_note || '').trim() || null,
        ...(!editingProduct && dupCandidates.length > 0
          ? { force_create: true, duplicate_override_reason: dupReason.trim() }
          : {}),
        standard_cost: form.standard_cost ? parseFloat(form.standard_cost) : null,
        case_size: form.case_size ? parseInt(form.case_size, 10) : null,
        category: form.category || null,
        // iter355b — send FK columns so backend _resolve_taxonomy() can keep
        // legacy + new columns in sync, and custom categories work end-to-end.
        category_id: form.category_id || null,
        subcategory_id: form.subcategory_id || null,
        // Post-mix: compute serving_ml and servings_per_unit from the selected measure.
        // iter_postmix v2 — input is servings per pmConvFactor_ml of concentrate
        // using the user-selected serving measure.
        // count-based mode: purchase unit has no ml — use pm_direct_servings_per_unit directly.
        is_post_mix: form.is_post_mix || false,
        post_mix_serving_ml: (() => {
          if (!form.is_post_mix) return null;
          const selUnit = form.units.find(u => u.unit_type === form.post_mix_selected_serving_unit && u.conversion_to_base);
          if (selUnit) return parseFloat(selUnit.conversion_to_base);
          const defSales = form.units.find(u => u.is_default_sales_unit && u.conversion_to_base);
          return defSales ? parseFloat(defSales.conversion_to_base) : null;
        })(),
        post_mix_servings_per_unit: (() => {
          if (!form.is_post_mix) return null;
          const purchUnit = form.units.find(u => u.is_default_purchase_unit);
          const purchMl = purchUnit?.conversion_to_base ? parseFloat(purchUnit.conversion_to_base) : null;
          // Count-based mode: no volume on purchase unit — use direct entry
          if (!purchMl || purchMl <= 0) {
            const v = parseFloat(form.pm_direct_servings_per_unit);
            return (!isNaN(v) && v > 0) ? v : null;
          }
          // Volume-based mode: existing formula
          if (!form.post_mix_servings_per_ref) return null;
          const spr = parseFloat(form.post_mix_servings_per_ref);
          if (isNaN(spr) || spr <= 0) return null;
          return spr * purchMl / pmConvFactor;
        })(),
        post_mix_finished_volume_ml: (() => {
          if (!form.is_post_mix) return null;
          const purchUnit = form.units.find(u => u.is_default_purchase_unit);
          const purchMl = purchUnit?.conversion_to_base ? parseFloat(purchUnit.conversion_to_base) : null;
          // Count-based mode: no volume known
          if (!purchMl || purchMl <= 0) return null;
          // Volume-based mode: existing formula
          if (!form.post_mix_servings_per_ref) return null;
          const spr = parseFloat(form.post_mix_servings_per_ref);
          if (isNaN(spr) || spr <= 0) return null;
          const selUnit = form.units.find(u => u.unit_type === form.post_mix_selected_serving_unit && u.conversion_to_base);
          const defSales = form.units.find(u => u.is_default_sales_unit && u.conversion_to_base);
          const servingMl = selUnit ? parseFloat(selUnit.conversion_to_base) : (defSales ? parseFloat(defSales.conversion_to_base) : null);
          if (!servingMl || servingMl <= 0) return null;
          return (spr * purchMl / pmConvFactor) * servingMl;
        })(),
        units: form.units.filter(u => u.unit_type.trim()).map(u => ({
          ..._stripUnit(u),
          conversion_to_base: u.conversion_to_base ? parseFloat(u.conversion_to_base) : null,
          // iter420 Phase 4 — yield data on the unit row
          waste_factor: u.waste_factor !== '' && u.waste_factor != null ? parseFloat(u.waste_factor) : null,
          observed_servings_per_unit: u.observed_servings_per_unit !== '' && u.observed_servings_per_unit != null ? parseFloat(u.observed_servings_per_unit) : null,
          // Stage 1A — case fields (null = keep stored value) and reviewed net grams
          case_qty: u.case_qty != null && u.case_qty !== '' ? parseInt(u.case_qty, 10) : null,
          unit_size_ml: u.unit_size_ml != null && u.unit_size_ml !== '' ? parseFloat(u.unit_size_ml) : null,
          net_grams: u.net_grams !== '' && u.net_grams != null ? parseFloat(u.net_grams) : null,
          net_grams_evidence: (u.net_grams_evidence || '').trim() || null,
        })),
        yields: yieldsForSave.filter(y => y.yield_type.trim()).map(y => ({
          ...y,
          servings_per_unit: y.servings_per_unit ? parseFloat(y.servings_per_unit) : null,
          ml_per_serving: y.ml_per_serving ? parseFloat(y.ml_per_serving) : null,
          nominal_servings_per_unit: y.nominal_servings_per_unit ? parseFloat(y.nominal_servings_per_unit) : null,
          nominal_ml_per_serving: y.nominal_ml_per_serving ? parseFloat(y.nominal_ml_per_serving) : null,
          waste_factor: y.waste_factor ? parseFloat(y.waste_factor) : null,
        })),
      };
      // Remove UI-only state field not in the backend schema
      delete payload.pm_direct_servings_per_unit;

      if (editingProduct) {
        // Stage 1A — read-only impact review first; size/case/base changes need an explicit OK
        const review = await beverageProductsService.impactReview(editingProduct.id, payload);
        if (review?.requires_confirmation) {
          setImpactReview(review);
          setPendingPayload(payload);
          return;
        }
        await commitSave(payload);
        return;
      }
      await beverageProductsService.create(payload);
      toast({ title: 'Created', description: `${form.product_name} added to master list` });
      setDialogOpen(false);
      loadProducts();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && Array.isArray(detail.candidates)) {
        setDupCandidates(detail.candidates);
        toast({ title: 'Possible duplicate', description: detail.message, variant: 'destructive' });
      } else {
        toast({
          title: 'Error',
          description: getApiError(err, 'Failed to save product'),
          variant: 'destructive',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const commitSave = async (payload) => {
    setSaving(true);
    try {
      const resp = await beverageProductsService.update(editingProduct.id, payload);
      const assignedCode = resp?.product_code;
      const hadNoCode = !((editingProduct.product_code || '').trim());
      if (hadNoCode && assignedCode) {
        toast({ title: 'Updated', description: `${form.product_name} updated — code ${assignedCode} assigned` });
      } else {
        toast({ title: 'Updated', description: `${form.product_name} updated` });
      }
      setImpactReview(null);
      setPendingPayload(null);
      setDialogOpen(false);
      loadProducts();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save product'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Release blocker — Post-Mix Concentrate (liquid dilution) can't be
  // combined with ANY non-volume base — count (Packet/Case/each) OR mass
  // (g/kg, e.g. a packaged snack sold by weight). Fails closed in the
  // form: the checkbox is disabled whenever any unit uses a non-volume
  // base, and is auto-unticked if a unit is switched to non-volume while
  // Post-Mix was already ticked (e.g. via the "Use packet + case units"
  // helper, or editing an existing unit's base). 2026-11 fix — this used
  // to only check for 'count', so a mass-based packaged snack (e.g. a 40g
  // crisp packet) could still have Post-Mix ticked and would then display
  // a nonsensical "Packet (40 ml)" label.
  const isSnackCountPattern = form.units.some(u => {
    const mt = measureTypeOf(u.base_unit);
    return mt !== 'volume' && mt !== 'unknown';
  });
  useEffect(() => {
    if (isSnackCountPattern && form.is_post_mix) {
      setForm(f => ({
        ...f, is_post_mix: false, post_mix_servings_per_ref: '',
        post_mix_selected_serving_unit: '', pm_direct_servings_per_unit: '',
      }));
      toast({
        title: 'Post-Mix Concentrate removed',
        description: "Post-Mix Concentrate can't be combined with non-liquid units (weight or count). Untick it before switching units, or keep it on a liquid pattern.",
        variant: 'destructive',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSnackCountPattern]);

  // Stage 1A — advisory duplicate check while creating (debounced)
  const _dupSize = (form.units.find(u => u.is_default_count_unit && u.conversion_to_base) || {}).conversion_to_base;
  useEffect(() => {
    if (!dialogOpen || editingProduct) return undefined;
    const name = (form.product_name || '').trim();
    if (name.length < 3 && !(form.barcode || '').trim()) { setDupCandidates([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const params = { name };
        if ((form.brand || '').trim()) params.brand = form.brand.trim();
        if ((form.barcode || '').trim()) params.barcode = form.barcode.trim();
        if (_dupSize) params.size = parseFloat(_dupSize);
        const r = await beverageProductsService.duplicateCheck(params);
        setDupCandidates(r?.candidates || []);
      } catch (_) { /* advisory only */ }
    }, 450);
    return () => clearTimeout(t);
  }, [dialogOpen, editingProduct, form.product_name, form.brand, form.barcode, _dupSize]);

  const _useExisting = (c) => {
    setDialogOpen(false);
    setSearchInput(c.product_name);
    setSearch(c.product_name);
    toast({ title: 'Using existing product', description: `${c.product_name} — no new product was created.` });
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.product_name}"? This cannot be undone.`)) return;
    try {
      await beverageProductsService.delete(product.id);
      toast({ title: 'Deleted', description: `${product.product_name} removed` });
      loadProducts();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to delete'), variant: 'destructive' });
    }
  };

  // ── Import handler ──
  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportProgress(null);
    try {
      const result = await beverageProductsService.importXlsx(importFile);
      setImportTaskId(result.task_id);
      setImportProgress({
        status: 'processing',
        total_rows: result.total_rows,
        processed: 0,
        rows_excluded: result.rows_excluded,
      });
      // Start polling
      pollImportStatus(result.task_id);
    } catch (err) {
      const msg = err.response?.data?.detail;
      const detail = typeof msg === 'string' ? msg : Array.isArray(msg) ? msg[0]?.msg : 'Upload failed';
      toast({ title: 'Import Error', description: detail, variant: 'destructive' });
      setImporting(false);
    }
  };

  const pollImportStatus = (taskId) => {
    let errorCount = 0;
    const interval = setInterval(async () => {
      try {
        const status = await beverageProductsService.importStatus(taskId);
        errorCount = 0;
        setImportProgress(status);
        if (status.status === 'complete' || status.status === 'failed') {
          clearInterval(interval);
          setImporting(false);
          if (status.status === 'complete') {
            loadProducts();
          }
        }
      } catch (err) {
        errorCount++;
        console.warn(`Import poll error #${errorCount}:`, err?.message);
        if (errorCount >= 5) {
          clearInterval(interval);
          setImporting(false);
          setImportProgress(prev => ({
            ...prev,
            status: 'failed',
            error: 'Lost connection to import task. The import may still be running in the background. Refresh the page to check.',
          }));
        }
      }
    }, 2000);
  };

  const resetImportDialog = () => {
    setImportDialogOpen(false);
    setImportFile(null);
    setImporting(false);
    setImportTaskId(null);
    setImportProgress(null);
  };

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
    setPage(1);
  };

  // Extract default unit and yield from a product's nested data
  const getDefaults = useMemo(() => {
    return (p) => {
      const defUnit = p.units?.find(u => u.is_default_count_unit) || p.units?.[0];
      const defYield = p.yields?.find(y => y.is_default) || p.yields?.[0];
      return {
        unitType: defUnit?.unit_type || '—',
        size: defUnit?.conversion_to_base != null ? defUnit.conversion_to_base : '—',
        baseUnit: defUnit?.base_unit || '—',
        yieldVal: defYield?.nominal_servings_per_unit ?? defYield?.servings_per_unit ?? '—',
      };
    };
  }, []);

  const SortHeader = ({ col, children, className = '' }) => {
    const active = sortBy === col;
    return (
      <TableHead
        className={`cursor-pointer select-none hover:bg-muted/60 transition-colors ${className}`}
        onClick={() => toggleSort(col)}
        data-testid={`sort-${col}`}
      >
        <span className="inline-flex items-center gap-1">
          {children}
          {active ? (
            sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
          ) : (
            <ArrowUpDown className="h-3 w-3 opacity-30" />
          )}
        </span>
      </TableHead>
    );
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6" data-testid="beverage-stock-master-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking/dashboard')}
            data-testid="back-to-dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="page-title">Beverage Stock Master</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Central product inventory — {total} product{total !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportDialogOpen(true)} data-testid="import-products-btn">
              <Upload className="h-4 w-4 mr-2" /> Import Excel
            </Button>
            <Button onClick={openCreate} data-testid="add-product-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Product
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search name, code, brand, sub-category..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                data-testid="product-search"
              />
            </div>
            <Select value={filterCategory || 'all'} onValueChange={(v) => { setFilterCategory(v === 'all' ? '' : v); setFilterSubcategory(''); setPage(1); }}>
              <SelectTrigger className="w-[170px]" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categoryOptions.map(c => (
                  <SelectItem key={c.id ?? c.slug} value={c.id ? String(c.id) : c.slug}>
                    {c.label}{!c.is_system && c.id ? ' (custom)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSubcategory || 'all'} onValueChange={(v) => { setFilterSubcategory(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[180px]" data-testid="filter-subcategory">
                <SelectValue placeholder="Sub-Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sub-Categories</SelectItem>
                {/* If a real category is selected, show its taxonomy subs (FK ids).
                    Otherwise fall back to the distinct legacy subcategory names
                    returned by /beverage-products so filtering still works. */}
                {filterCategoryOption && filterCategoryOption.subcategories.length > 0
                  ? filterCategoryOption.subcategories.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                    ))
                  : subcategoryOptions.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus || 'all'} onValueChange={(v) => { setFilterStatus(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[120px]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Product Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16" data-testid="products-loading">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground" data-testid="products-empty">
              <Package className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No products found</p>
              <p className="text-sm mt-1">{search || filterCategory || filterSubcategory ? 'Try adjusting your filters' : 'Add your first beverage product to get started'}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40 text-xs">
                      <SortHeader col="product_name">Product Name</SortHeader>
                      <SortHeader col="product_code" className="hidden sm:table-cell">Code</SortHeader>
                      <SortHeader col="brand" className="hidden md:table-cell">Brand</SortHeader>
                      <SortHeader col="category" className="hidden md:table-cell">Category</SortHeader>
                      <SortHeader col="subcategory" className="hidden lg:table-cell">Sub-Category</SortHeader>
                      <TableHead className="hidden lg:table-cell text-xs">Type</TableHead>
                      <TableHead className="hidden lg:table-cell text-xs text-right">Size</TableHead>
                      <TableHead className="hidden lg:table-cell text-xs">Unit</TableHead>
                      <TableHead className="hidden xl:table-cell text-xs text-right">Yield</TableHead>
                      <SortHeader col="status" className="text-center">Status</SortHeader>
                      {canManage && <TableHead className="w-[70px]"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map(p => {
                      const d = getDefaults(p);
                      return (
                        <TableRow key={p.id} className="cursor-pointer hover:bg-muted/30"
                          onClick={() => canManage ? openEdit(p) : null}
                          data-testid={`product-row-${p.id}`}>
                          <TableCell className="font-medium text-sm" data-testid={`product-name-${p.id}`}>{p.product_name}</TableCell>
                          <TableCell className="hidden sm:table-cell text-sm text-muted-foreground" data-testid={`product-code-${p.id}`}>{p.product_code || '—'}</TableCell>
                          <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{p.brand || '—'}</TableCell>
                          <TableCell className="hidden md:table-cell">
                            {(() => {
                              // For PSB-created products: legacy `category` enum may be NULL while
                              // `category_id` is set. Fall back to the taxonomy name so the column
                              // never shows '—' for a product whose category is known via FK.
                              const catLabel = p.category
                                ? p.category.replace(/_/g, ' ')
                                : (taxonomyCategories.find(c => c.id === p.category_id)?.name || null);
                              return catLabel
                                ? <Badge variant="outline" className="text-[10px] capitalize whitespace-nowrap">{catLabel}</Badge>
                                : '—';
                            })()}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{p.subcategory || '—'}</TableCell>
                          <TableCell className="hidden lg:table-cell text-sm text-muted-foreground capitalize">{d.unitType}</TableCell>
                          <TableCell className="hidden lg:table-cell text-sm text-muted-foreground text-right tabular-nums">{d.size}</TableCell>
                          <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{d.baseUnit}</TableCell>
                          <TableCell className="hidden xl:table-cell text-sm text-muted-foreground text-right tabular-nums">{d.yieldVal}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant={p.status === 'active' ? 'default' : 'secondary'} className="text-[10px]">
                              {p.status}
                            </Badge>
                          </TableCell>
                          {canManage && (
                            <TableCell className="text-center">
                              <div className="flex gap-0.5 justify-center">
                                <Button variant="ghost" size="icon" className="h-7 w-7"
                                  onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                                  data-testid={`edit-product-${p.id}`}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7"
                                  onClick={(e) => { e.stopPropagation(); handleDelete(p); }}
                                  data-testid={`delete-product-${p.id}`}>
                                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1}
                      onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">{page} / {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages}
                      onClick={() => setPage(p => p + 1)} data-testid="next-page">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Create / Edit Dialog ──────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 space-y-6 py-2 pr-1" style={{ maxHeight: 'calc(92vh - 140px)' }}>
            {/* Standard product fields — shared with PSB Stage 1B (AddProductFormBody) */}
            <AddProductFormBody
              form={form}
              setForm={setForm}
              categoryOptions={categoryOptions}
              dynamicUnitTypes={dynamicUnitTypes}
              dynamicUnitLabels={dynamicUnitLabels}
              dynamicUnitMlMap={dynamicUnitMlMap}
              showCost
              showBarcode
              showStatus
              showCaseSize
              onUnitChange={handleUnitChange}
              isCreate={!editingProduct}
              duplicateReview={!editingProduct ? (
                <DuplicateReviewPanel
                  candidates={dupCandidates}
                  reason={dupReason}
                  onReasonChange={setDupReason}
                  onOpen={(c) => openEdit(c)}
                  onUse={_useExisting}
                />
              ) : null}
            />
              {/* Post-Mix Concentrate */}
              <div className={`space-y-2 rounded-lg border border-dashed p-3 sm:col-span-2 ${isSnackCountPattern ? 'border-slate-300 bg-slate-50' : 'border-amber-300 bg-amber-50'}`}>
                <div className="flex items-center gap-2">
                  <input
                    id="is_post_mix"
                    type="checkbox"
                    checked={!!form.is_post_mix}
                    disabled={isSnackCountPattern}
                    onChange={(e) => setForm(f => ({
                      ...f,
                      is_post_mix: e.target.checked,
                      post_mix_servings_per_ref: e.target.checked ? f.post_mix_servings_per_ref : '',
                      post_mix_selected_serving_unit: e.target.checked ? f.post_mix_selected_serving_unit : '',
                      pm_direct_servings_per_unit: e.target.checked ? f.pm_direct_servings_per_unit : '',
                    }))}
                    className={`w-4 h-4 rounded border-slate-300 ${isSnackCountPattern ? 'cursor-not-allowed opacity-50' : 'text-amber-600 cursor-pointer'}`}
                    data-testid="checkbox-is-post-mix"
                  />
                  <Label htmlFor="is_post_mix" className={`font-semibold ${isSnackCountPattern ? 'text-slate-500 cursor-not-allowed' : 'text-amber-800 cursor-pointer'}`}>
                    Post-Mix Concentrate
                  </Label>
                </div>
                {isSnackCountPattern ? (
                  <p className="text-xs text-slate-600 leading-snug" data-testid="post-mix-disabled-count-note">
                    Disabled — this product uses weight or count-based units (e.g. Packet/Case by g/kg or each).
                    Post-Mix Concentrate is a liquid-dilution concept and can't be combined with those units.
                  </p>
                ) : (
                <>
                <p className="text-[10px] text-amber-700 leading-snug">
                  Tick when this product is a post-mix concentrate. Sales Wizard entries will convert
                  serving counts to concentrate depletion automatically.
                </p>
                {form.is_post_mix && (() => {
                  const purchaseUnit = form.units.find(u => u.is_default_purchase_unit && u.unit_type);
                  const purchaseUnitMl = purchaseUnit ? parseFloat(purchaseUnit.conversion_to_base) : null;

                  // All units with a defined ml value available as serving measures
                  const servingOptions = form.units.filter(u => u.unit_type && u.conversion_to_base && parseFloat(u.conversion_to_base) > 0);

                  // Currently selected serving measure (default to default_sales_unit if not set)
                  const defaultSalesUnit = form.units.find(u => u.is_default_sales_unit && u.conversion_to_base);
                  const selectedServingUnit = servingOptions.find(u => u.unit_type === form.post_mix_selected_serving_unit)
                    || defaultSalesUnit
                    || servingOptions[0];
                  const selectedServingMl = selectedServingUnit ? parseFloat(selectedServingUnit.conversion_to_base) : null;

                  // Display label for purchase unit
                  const purchaseLabel = purchaseUnit
                    ? `${purchaseUnit.unit_type}${purchaseUnitMl ? ` (${purchaseUnitMl.toLocaleString()} ml)` : ''}`
                    : null;

                  const refVolLabel = (pmConvFactor / 1000) % 1 === 0
                    ? `${pmConvFactor / 1000}L`
                    : `${(pmConvFactor / 1000).toFixed(2)}L`;

                  // Count-based: purchase unit has no ml — enter servings per unit directly
                  const isCountBased = !purchaseUnitMl || purchaseUnitMl <= 0;

                  // Volume-based computed values
                  const sprVal = form.post_mix_servings_per_ref ? parseFloat(form.post_mix_servings_per_ref) : null;
                  const approxServingsPerUnit = (!isCountBased && sprVal && purchaseUnitMl && purchaseUnitMl > 0)
                    ? sprVal * purchaseUnitMl / pmConvFactor
                    : null;
                  const approxFinishedMl = (approxServingsPerUnit && selectedServingMl)
                    ? approxServingsPerUnit * selectedServingMl
                    : null;

                  // Handler: when user changes serving measure, auto-convert the yield value (volume-based only)
                  const handleServingMeasureChange = (newUnitType) => {
                    const newUnit = servingOptions.find(u => u.unit_type === newUnitType);
                    const newMl = newUnit ? parseFloat(newUnit.conversion_to_base) : null;
                    const oldMl = selectedServingMl;
                    if (oldMl && newMl && oldMl !== newMl && form.post_mix_servings_per_ref) {
                      const converted = Math.round(parseFloat(form.post_mix_servings_per_ref) * oldMl / newMl * 100) / 100;
                      setForm(f => ({
                        ...f,
                        post_mix_selected_serving_unit: newUnitType,
                        post_mix_servings_per_ref: String(converted),
                      }));
                    } else {
                      setForm(f => ({ ...f, post_mix_selected_serving_unit: newUnitType }));
                    }
                  };

                  // Count-based: direct servings entry
                  const directServings = form.pm_direct_servings_per_unit ? parseFloat(form.pm_direct_servings_per_unit) : null;

                  return (
                    <div className="space-y-3 mt-2">
                      {/* Row 1: Purchase Unit (read-only indicator) */}
                      <div className="rounded bg-white border border-amber-200 px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-slate-700">Purchase / Count Unit</span>
                          {purchaseLabel
                            ? <span className="text-xs font-semibold text-slate-800" data-testid="pm-purchase-unit-display">
                                {purchaseLabel}
                              </span>
                            : <span className="text-xs text-amber-600 italic">— Not set. Mark a unit as Purchase below.</span>
                          }
                        </div>
                        {!isCountBased && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            Defined in Units of Measure below. Used to scale the yield.
                          </p>
                        )}
                      </div>

                      {/* COUNT-BASED MODE: compact "1 Tub = N Each" relationship */}
                      {isCountBased ? (
                        <div className="space-y-2 mt-1">
                          {/* Single-row: [1] [Tub] [=] [50 input] [Serving] [info tooltip] */}
                          <div className="flex items-center gap-2 rounded border border-amber-200 bg-white px-3 py-2">
                            <span className="text-xs text-slate-400 shrink-0">1</span>
                            <span className="text-sm font-semibold text-slate-800 shrink-0">
                              {purchaseUnit?.unit_type || '—'}
                            </span>
                            <span className="text-xs text-slate-400 shrink-0">=</span>
                            <Input
                              id="pm_direct_servings_per_unit"
                              type="number"
                              step="1"
                              min="1"
                              value={form.pm_direct_servings_per_unit}
                              onChange={(e) => setForm(f => ({ ...f, pm_direct_servings_per_unit: e.target.value }))}
                              placeholder="50"
                              className="w-20 h-7 text-sm text-center tabular-nums"
                              data-testid="input-pm-direct-servings"
                            />
                            <span className="text-sm font-semibold text-slate-800 shrink-0">
                              {form.units.find(u => u.is_default_sales_unit)?.unit_type
                                || form.units.find(u => u.is_sales_unit)?.unit_type
                                || 'Each'}
                            </span>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="w-3.5 h-3.5 text-amber-500 cursor-help shrink-0" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs text-xs leading-snug">
                                  <p className="font-medium mb-0.5">Engine formula</p>
                                  <p>Servings recorded ÷ yield = concentrate depleted. Provisional — not manufacturer-verified. Do not change with an active session open.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          {/* Single helper line */}
                          <p className="text-[10px] text-amber-700 leading-snug">
                            How many{' '}
                            <span className="font-medium">
                              {form.units.find(u => u.is_default_sales_unit)?.unit_type
                                || form.units.find(u => u.is_sales_unit)?.unit_type
                                || 'servings'}
                            </span>
                            {' '}each{' '}
                            <span className="font-medium">{purchaseUnit?.unit_type || 'unit'}</span>
                            {' '}yields — used by the Sales Wizard for reconciliation.
                          </p>
                          {/* Inline validation only */}
                          {(!form.pm_direct_servings_per_unit || parseFloat(form.pm_direct_servings_per_unit) <= 0) && (
                            <p className="text-[10px] text-rose-600">Enter a yield to enable stock reconciliation.</p>
                          )}
                        </div>
                      ) : (
                        <>
                          {/* VOLUME-BASED MODE: existing Row 2 + Row 3 + Row 4 */}
                          {/* Row 2: Serving Measure dropdown */}
                          <div className="space-y-1">
                            <Label className="text-xs font-medium">
                              Serving Measure{' '}
                              <span className="text-muted-foreground font-normal">(for yield entry)</span>
                            </Label>
                            {servingOptions.length > 0 ? (
                              <Select
                                value={selectedServingUnit?.unit_type || ''}
                                onValueChange={handleServingMeasureChange}
                              >
                                <SelectTrigger className="w-56 h-8 text-xs" data-testid="postmix-serving-measure-select">
                                  <SelectValue placeholder="Select measure..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {servingOptions.map(u => (
                                    <SelectItem key={u.unit_type} value={u.unit_type}>
                                      {u.unit_type.charAt(0).toUpperCase() + u.unit_type.slice(1)}
                                      {u.conversion_to_base ? ` (${parseFloat(u.conversion_to_base).toLocaleString()} ml)` : ''}
                                      {u.is_default_sales_unit ? ' ★' : ''}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <p className="text-xs text-amber-600 italic">
                                — No units with ml defined. Add units below first.
                              </p>
                            )}
                            <p className="text-[10px] text-muted-foreground leading-snug">
                              Choose the serving size for which you&apos;ll enter the yield count.
                              Changing this auto-converts the number below. ★ = default sales unit.
                            </p>
                          </div>

                          {/* Row 3: Servings per ref volume input */}
                          <div className="space-y-1">
                            <Label htmlFor="post_mix_servings_per_ref" className="text-xs font-medium">
                              {selectedServingUnit
                                ? `${selectedServingUnit.unit_type.charAt(0).toUpperCase() + selectedServingUnit.unit_type.slice(1)} servings per ${refVolLabel} of concentrate`
                                : `Servings per ${refVolLabel} of concentrate`
                              }
                            </Label>
                            <div className="flex items-center gap-2">
                              <Input
                                id="post_mix_servings_per_ref"
                                type="number"
                                step="0.1"
                                min="0"
                                value={form.post_mix_servings_per_ref}
                                onChange={(e) => setForm(f => ({ ...f, post_mix_servings_per_ref: e.target.value }))}
                                placeholder="e.g. 21.1"
                                className="w-32"
                                data-testid="input-post-mix-servings-per-ref"
                              />
                              {selectedServingUnit && (
                                <span className="text-sm text-muted-foreground">
                                  {selectedServingUnit.unit_type}
                                  {selectedServingMl ? ` servings (${selectedServingMl} ml each)` : ' servings'}
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-muted-foreground leading-snug">
                              How many {selectedServingUnit?.unit_type || 'servings'} does {refVolLabel} of concentrate produce?
                              {selectedServingMl && ` (1 serving = ${selectedServingMl} ml)`}
                            </p>
                          </div>

                          {/* Row 4: Calculated summary (read-only) */}
                          {approxServingsPerUnit != null && selectedServingMl && (
                            <div className="rounded bg-amber-100 border border-amber-200 px-3 py-2 space-y-1">
                              <p className="text-xs font-semibold text-amber-800">Calculated yield per purchase unit</p>
                              <div className="flex flex-wrap gap-x-6 gap-y-0.5">
                                <span className="text-xs text-amber-900">
                                  <span className="font-bold tabular-nums" data-testid="pm-approx-servings">{approxServingsPerUnit.toFixed(1)}</span>
                                  {' '}{selectedServingUnit?.unit_type || 'serving'} servings
                                </span>
                                {approxFinishedMl && (
                                  <span className="text-xs text-amber-700">
                                    ≈ {(approxFinishedMl / 1000).toFixed(2)} L finished drink
                                  </span>
                                )}
                              </div>
                              {purchaseLabel && (
                                <p className="text-[10px] text-amber-600">
                                  From 1 × {purchaseLabel} of concentrate
                                </p>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()}
                </>
                )}
              </div>
            {/* iter420 Phase 4 — Yield rows are now unified into the
                Units table above (Observed/Unit + Waste %). The
                Advanced panel below remains for "orphan" yields whose
                ml_per_serving doesn't match any Unit row — typically
                legacy data we don't want to silently drop. Hidden
                entirely when no orphans exist. */}
            {(() => {
              const unitMls = new Set(
                form.units
                  .filter(u => u.unit_type?.trim() && u.conversion_to_base)
                  .map(u => parseFloat(u.conversion_to_base))
              );
              const orphanYields = form.yields.filter(y => {
                const m = parseFloat(y.ml_per_serving);
                return m && !unitMls.has(m);
              });
              if (orphanYields.length === 0) return null;
              // Detect count-based so the YieldRow can suppress vol/ml warnings
              const _purchUnit = form.units.find(u => u.is_default_purchase_unit && u.unit_type);
              const _purchMl = _purchUnit ? parseFloat(_purchUnit.conversion_to_base) : null;
              const _isCountBased = !_purchMl || _purchMl <= 0;
              return (
                <details className="space-y-3 group" open data-testid="yields-advanced-details">
                  <summary className="text-sm font-semibold cursor-pointer flex items-center gap-2 select-none">
                    <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                    Legacy Yields — Unmatched to a Unit row
                    <span className="text-xs text-muted-foreground font-normal">({orphanYields.length})</span>
                  </summary>
                  <div className="space-y-3 pt-2">
                    <p className="text-xs text-muted-foreground">
                      These yield rows don&apos;t correspond to any Unit defined above. Add a matching Unit row
                      (with the same ml as the yield&apos;s <em>ml/Serving</em>) and tick <strong>Sales</strong>
                      to migrate the data inline — then delete the orphan row here.
                    </p>
                <div className="flex items-center justify-end">
                  <Button variant="outline" size="sm" onClick={() => setForm(f => ({ ...f, yields: [...f.yields, emptyYield()] }))}
                    data-testid="add-yield-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Yield override
                  </Button>
                </div>
              <div className="border rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 text-xs">
                      <TableHead className="text-xs">Yield Type</TableHead>
                      <TableHead className="text-xs" title="Spec-sheet servings per unit. Populated from Excel import (e.g. 88 pints per 50L keg).">Nominal Servings/Unit</TableHead>
                      <TableHead className="text-xs" title="Actual observed servings (optional — overrides nominal for calculations if set)">Actual Servings/Unit</TableHead>
                      <TableHead className="text-xs">ml/Serving</TableHead>
                      <TableHead className="text-xs">Waste %</TableHead>
                      <TableHead className="text-xs text-center">Default</TableHead>
                      <TableHead className="text-xs text-center w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.yields.map((y, i) => (
                      <YieldRow key={i} yld={y} index={i} isOnly={form.yields.length === 1}
                        units={form.units}
                        isCountBased={form.is_post_mix ? _isCountBased : false}
                        onChange={handleYieldChange}
                        onRemove={(idx) => setForm(f => ({ ...f, yields: f.yields.filter((_, j) => j !== idx) }))} />
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground">
                <strong>Nominal Servings/Unit</strong> is now auto-computed from the count Unit ÷ ml/Serving and
                shown read-only. Populate <strong>Actual Servings/Unit</strong> only when observed yields differ.
              </p>
              </div>
            </details>
              );
            })()}
          </div>

          <DialogFooter className="border-t pt-3 gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-product-btn">
              <X className="h-4 w-4 mr-1.5" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-product-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              {editingProduct ? 'Save Changes' : 'Create Product'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stage 1A — impact review before a size / case / base change is saved */}
      <ImpactReviewDialog
        review={impactReview}
        open={!!impactReview}
        saving={saving}
        onCancel={() => { setImpactReview(null); setPendingPayload(null); }}
        onConfirm={() => pendingPayload && commitSave(pendingPayload)}
      />

      {/* ── Import Dialog ─────────────────────────────────── */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => { if (!importing) { if (!open) resetImportDialog(); else setImportDialogOpen(true); } }}>
        <DialogContent className="max-w-lg" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" /> Import Products from Excel
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {!importProgress ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Upload an Excel file with an <strong>Import_Clean</strong> sheet. 
                  Rows with <code>Include_in_Import = Yes</code> will be imported.
                  Existing products (matched by code or name) will be updated.
                </p>
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    id="import-file-input"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    data-testid="import-file-input"
                  />
                  <label htmlFor="import-file-input" className="cursor-pointer space-y-2">
                    <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                    <p className="text-sm font-medium">
                      {importFile ? importFile.name : 'Click to select Excel file'}
                    </p>
                    {importFile && (
                      <p className="text-xs text-muted-foreground">
                        {(importFile.size / 1024).toFixed(1)} KB
                      </p>
                    )}
                  </label>
                </div>
              </>
            ) : importProgress.status === 'processing' ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Importing products...</p>
                    <p className="text-xs text-muted-foreground">
                      {importProgress.processed || 0} of {importProgress.total_rows} rows processed
                    </p>
                  </div>
                </div>
                <Progress
                  value={importProgress.total_rows ? ((importProgress.processed || 0) / importProgress.total_rows) * 100 : 0}
                  className="h-2"
                  data-testid="import-progress-bar"
                />
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="bg-muted/50 rounded p-2">
                    <p className="font-semibold text-green-600">{importProgress.products_created || 0}</p>
                    <p className="text-muted-foreground">Created</p>
                  </div>
                  <div className="bg-muted/50 rounded p-2">
                    <p className="font-semibold text-blue-600">{importProgress.products_updated || 0}</p>
                    <p className="text-muted-foreground">Updated</p>
                  </div>
                  <div className="bg-muted/50 rounded p-2">
                    <p className="font-semibold text-amber-600">{importProgress.rows_skipped || 0}</p>
                    <p className="text-muted-foreground">Skipped</p>
                  </div>
                </div>
              </div>
            ) : importProgress.status === 'complete' ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-green-600">
                  <CheckCircle2 className="h-6 w-6" />
                  <div>
                    <p className="font-semibold">Import Complete</p>
                    <p className="text-xs text-muted-foreground">All rows processed successfully</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
                  <div className="bg-green-50 rounded p-2 border border-green-200">
                    <p className="font-bold text-green-700 text-lg">{importProgress.products_created}</p>
                    <p className="text-green-600">Created</p>
                  </div>
                  <div className="bg-blue-50 rounded p-2 border border-blue-200">
                    <p className="font-bold text-blue-700 text-lg">{importProgress.products_updated}</p>
                    <p className="text-blue-600">Updated</p>
                  </div>
                  <div className="bg-amber-50 rounded p-2 border border-amber-200">
                    <p className="font-bold text-amber-700 text-lg">{importProgress.rows_skipped}</p>
                    <p className="text-amber-600">Skipped</p>
                  </div>
                  <div className="bg-gray-50 rounded p-2 border border-gray-200">
                    <p className="font-bold text-gray-700 text-lg">{importProgress.rows_excluded}</p>
                    <p className="text-gray-600">Excluded</p>
                  </div>
                </div>
                {importProgress.categories_used?.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    Categories: {importProgress.categories_used.join(', ')}
                  </div>
                )}
                {importProgress.skipped_details?.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-amber-600 font-medium">
                      View skipped rows ({importProgress.skipped_details.length})
                    </summary>
                    <div className="mt-2 max-h-32 overflow-y-auto space-y-1 bg-muted/30 rounded p-2">
                      {importProgress.skipped_details.map((s, i) => (
                        <p key={i}>Row {s.row}: {s.reason} {s.name ? `(${s.name})` : ''}</p>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ) : importProgress.status === 'failed' ? (
              <div className="flex items-center gap-3 text-destructive">
                <AlertCircle className="h-6 w-6" />
                <div>
                  <p className="font-semibold">Import Failed</p>
                  <p className="text-xs">{importProgress.error || 'Unknown error'}</p>
                </div>
              </div>
            ) : null}
          </div>

          <DialogFooter className="gap-2">
            {!importProgress ? (
              <>
                <Button variant="outline" onClick={resetImportDialog} data-testid="cancel-import-btn">
                  Cancel
                </Button>
                <Button onClick={handleImport} disabled={!importFile || importing} data-testid="start-import-btn">
                  {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                  Start Import
                </Button>
              </>
            ) : importProgress.status === 'processing' ? (
              <p className="text-xs text-muted-foreground">Please wait, do not close this dialog...</p>
            ) : (
              <Button onClick={resetImportDialog} data-testid="close-import-btn">
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
